import { useCallback, useEffect, useRef, useState } from "react";
import * as O from "@/components/Ornaments";
import { useSound } from "@/lib/sound";
import {
  CountdownSection,
  StorySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import type { VariantProps } from "./types";

// ═══════════════════════════════════════════════════════════════════════
// MASTERCLASS — c06 "Murabbiy". A single-instructor workshop site.
//
// Voice: educational, classical, bookish. The whole page should feel
// like leafing through a printed programme for a master's lecture: stone
// paper, charcoal ink, a quiet thread of warm gold, Marcellus serif. No
// music — focus belongs to the instructor's voice. The hero is a single
// large portrait with the instructor's name italic-drawn beneath via a
// clip-path inset reveal (centre-out, 1.6s), with a soft gold underline
// that follows. Two bespoke sections — Curriculum (4 modules) and an
// Instructor profile with portrait + past clients — sit between the
// shared sections. Sound is restrained: a tap on RSVP, a hover-pop on
// curriculum modules, a soft tap on share buttons. Reduced-motion
// users get the static composition.
// ═══════════════════════════════════════════════════════════════════════

const GOLD = "#B8975A";
const INSTRUCTOR_NAME = "Erkin Yusupov";
const INSTRUCTOR_BIO_LINE =
  "15 yillik tajribali senior product designer · Toshkent / Berlin";

// ─── prefers-reduced-motion hook (no extra deps) ──────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduce;
}

// ─── IntersectionObserver reveal wrapper ──────────────────────────────
interface RevealProps {
  children: React.ReactNode;
  reduce: boolean;
  delay?: number;
}
function Reveal({ children, reduce, delay = 0 }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(reduce);
  useEffect(() => {
    if (reduce) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);
  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(20px)",
        transition: reduce
          ? "none"
          : `opacity 1.0s ease-out ${delay}ms, transform 1.0s ease-out ${delay}ms`,
        willChange: reduce ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── Hairline divider with gold diamond ───────────────────────────────
function HairlineDivider({ ink }: { ink: string }) {
  return (
    <div
      aria-hidden
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 24px",
        gap: 16,
      }}
    >
      <div style={{ flex: 1, maxWidth: 120, height: 1, background: ink, opacity: 0.25 }} />
      <O.Diamond size={9} color={GOLD} />
      <div style={{ flex: 1, maxWidth: 120, height: 1, background: ink, opacity: 0.25 }} />
    </div>
  );
}

// ─── Curriculum / module data ─────────────────────────────────────────
interface ModuleEntry {
  no: string;
  duration: string;
  title: string;
  body: string;
}
const MODULES: ModuleEntry[] = [
  {
    no: "01",
    duration: "2 soat",
    title: "UI fundamentals",
    body: "Grid, tipografiya, rang. Interfeysning ko'zga ko'rinmas suyagi — qaysi qoidalar ishlaydi va nima uchun.",
  },
  {
    no: "02",
    duration: "2 soat",
    title: "Component systems",
    body: "Design tokens, atomic dizayn, komponentlar kutubxonasi. Bir marta qurib, butun mahsulot bo'ylab takrorlanadigan tizim.",
  },
  {
    no: "03",
    duration: "2 soat",
    title: "Animation language",
    body: "Mikro-interaksiyalar, easing, davomiylik. Interfeys foydalanuvchi bilan qanday gaplashishini animatsiya orqali o'rganamiz.",
  },
  {
    no: "04",
    duration: "1 soat",
    title: "Q&A · portfolio review",
    body: "Talabalarning portfoliosi bo'yicha jonli muhokama, savol-javob, individual maslahat.",
  },
];

// Past clients / studios where the instructor has worked. Plain text
// "logos" rendered as tracked-out small caps — keeps the page bookish
// and avoids shipping raster brand marks.
const CLIENTS = ["UZUM", "PAYME", "BEELINE", "IT PARK"];

// ─── Curriculum module card (with hover sound) ────────────────────────
interface ModuleCardProps {
  m: ModuleEntry;
  ink: string;
  bg: string;
  font: string;
  reduce: boolean;
  onHover: () => void;
}
function ModuleCard({ m, ink, bg, font, reduce, onHover }: ModuleCardProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onMouseEnter={() => {
        setHovered(true);
        onHover();
      }}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => {
        setHovered(true);
        onHover();
      }}
      onBlur={() => setHovered(false)}
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 28,
        alignItems: "start",
        padding: "32px 28px",
        background: hovered ? `${GOLD}10` : "transparent",
        borderTop: `1px solid ${ink}22`,
        transition: reduce ? "none" : "background 220ms ease-out",
        border: 0,
        borderTopWidth: 1,
        borderTopStyle: "solid",
        borderTopColor: `${ink}22`,
        textAlign: "left",
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        width: "100%",
      }}
      className="murabbiy-module"
    >
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 38,
          fontWeight: 400,
          color: GOLD,
          lineHeight: 1,
          minWidth: 64,
        }}
      >
        {m.no}
      </div>
      <div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: `${ink}99`,
            marginBottom: 8,
          }}
        >
          MODUL · {m.duration.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: "clamp(22px, 2.6vw, 30px)",
            fontWeight: 400,
            color: ink,
            marginBottom: 10,
            letterSpacing: "0.01em",
          }}
        >
          {m.title}
        </div>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 15,
            lineHeight: 1.65,
            color: `${ink}cc`,
            margin: 0,
            maxWidth: 560,
          }}
        >
          {m.body}
        </p>
      </div>
      <div
        className="murabbiy-module-duration"
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.28em",
          color: GOLD,
          paddingTop: 8,
          // Gold underline appears on hover — like a librarian's pencil.
          borderBottom: `1px solid ${hovered ? GOLD : "transparent"}`,
          transition: reduce ? "none" : "border-color 220ms ease-out",
          alignSelf: "center",
          opacity: 0.95,
          background: bg === "transparent" ? undefined : "transparent",
        }}
      >
        {m.duration}
      </div>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// VARIANT
// ═══════════════════════════════════════════════════════════════════════
export function MasterclassVariant({ t, isDark, ctx }: VariantProps) {
  const reduce = usePrefersReducedMotion();
  const { play } = useSound();

  // Hero "draws" the instructor's italic name on first paint via a
  // centre-out clip-path inset reveal, then the gold underline expands.
  const [drawn, setDrawn] = useState(reduce);
  const [underlined, setUnderlined] = useState(reduce);
  useEffect(() => {
    if (reduce) {
      setDrawn(true);
      setUnderlined(true);
      return;
    }
    const a = window.requestAnimationFrame(() => setDrawn(true));
    const b = window.setTimeout(() => setUnderlined(true), 1100);
    return () => {
      window.cancelAnimationFrame(a);
      window.clearTimeout(b);
    };
  }, [reduce]);

  // Sound — RSVP yes/no = tap, share buttons = tap, module hover = hover.
  const baseSetRsvp = ctx.rsvp.setRsvp;
  const setRsvpWithSound = useCallback(
    (v: string | null) => {
      if (v === "yes" || v === "no") play("tap");
      baseSetRsvp(v);
    },
    [baseSetRsvp, play],
  );

  const isMobile = useCallback(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  }, []);

  const onShareTap = useCallback(() => {
    if (isMobile()) return;
    play("tap");
  }, [play, isMobile]);

  const onModuleHover = useCallback(() => {
    if (isMobile()) return;
    play("hover");
  }, [play, isMobile]);

  const heroSrc = t.photos[0];
  const instructorPortraitSrc = t.photos[1] || t.photos[0];

  // Title pieces from the names string ("MASTERCLASS · ART OF UI").
  const parts = t.names.split("·").map((s) => s.trim());
  const eyebrowText = parts[0] || "MASTERCLASS";
  const topic = parts.slice(1).join(" · ") || t.names;

  return (
    <div className="murabbiy-variant">
      <style>{`
        .murabbiy-variant { background: ${t.bg}; color: ${t.ink}; }
        .murabbiy-variant button:focus-visible,
        .murabbiy-variant a:focus-visible {
          outline: 2px solid ${GOLD};
          outline-offset: 2px;
        }
        @media (max-width: 768px) {
          .murabbiy-variant button,
          .murabbiy-variant a[role="button"] { min-height: 44px; }
          .murabbiy-variant .murabbiy-hero {
            grid-template-columns: 1fr !important;
            min-height: auto !important;
          }
          .murabbiy-variant .murabbiy-hero-photo {
            min-height: 0 !important;
            height: min(56vw, 380px) !important;
            width: 100% !important;
            aspect-ratio: 4 / 3;
          }
          .murabbiy-variant .murabbiy-hero-text {
            padding: 64px 18px 48px !important;
            text-align: center !important;
          }
          .murabbiy-variant .murabbiy-topic {
            font-size: clamp(36px, 12vw, 72px) !important;
          }
          .murabbiy-variant .murabbiy-module {
            grid-template-columns: auto 1fr !important;
            gap: 12px 16px !important;
            padding: 24px 18px !important;
          }
          .murabbiy-variant .murabbiy-module-duration {
            display: none !important;
          }
          .murabbiy-variant .murabbiy-instructor-grid {
            grid-template-columns: 1fr !important;
          }
          .murabbiy-variant .murabbiy-instructor-photo {
            min-height: 0 !important;
            height: min(56vw, 320px) !important;
            aspect-ratio: 4 / 3;
          }
          .murabbiy-variant .murabbiy-instructor-text {
            padding: 40px 24px !important;
          }
          .murabbiy-variant .murabbiy-clients {
            justify-content: flex-start !important;
            gap: 14px !important;
            font-size: 10px !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .murabbiy-variant *,
          .murabbiy-variant *::before,
          .murabbiy-variant *::after {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      {/* ─── HERO ────────────────────────────────────────────────────── */}
      <section
        className="murabbiy-hero"
        style={{
          display: "grid",
          gridTemplateColumns: "1.1fr 1fr",
          minHeight: "100vh",
          background: t.bg,
          color: t.ink,
        }}
      >
        {/* Portrait — charcoal-tinted, classical */}
        <div
          className="murabbiy-hero-photo"
          style={{
            position: "relative",
            overflow: "hidden",
            background: t.ink,
            minHeight: "70vh",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url("${heroSrc}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "saturate(0.85) contrast(1.02)",
            }}
            aria-hidden
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(180deg, ${t.ink}55 0%, ${t.ink}99 100%)`,
              mixBlendMode: "multiply",
            }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(140% 100% at 30% 30%, transparent 35%, ${t.ink}55 100%)`,
            }}
          />

          {/* Caption etched on the photo */}
          <div
            style={{
              position: "absolute",
              left: "8%",
              right: "8%",
              bottom: "8%",
              color: t.bg,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.4em",
                opacity: 0.85,
                marginBottom: 8,
              }}
            >
              MURABBIY
            </div>
            <div
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(28px, 3.4vw, 44px)",
                fontWeight: 400,
                lineHeight: 1.05,
                letterSpacing: "0.02em",
              }}
            >
              {INSTRUCTOR_NAME}
            </div>
            <div style={{ width: 64, height: 1, background: GOLD, marginTop: 14, opacity: 0.9 }} />
          </div>

          {/* Right edge — gold thread between halves */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              right: 0,
              top: "8%",
              bottom: "8%",
              width: 1,
              background: GOLD,
              opacity: 0.55,
            }}
          />
        </div>

        {/* Text column */}
        <div
          className="murabbiy-hero-text"
          style={{
            position: "relative",
            padding: "96px 64px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* Eyebrow */}
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.46em",
              color: GOLD,
              marginBottom: 14,
            }}
          >
            {eyebrowText}
          </div>
          <div style={{ width: 36, height: 1, background: GOLD, marginBottom: 36, opacity: 0.85 }} />

          {/* Topic */}
          <h1
            className="murabbiy-topic"
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(48px, 7vw, 96px)",
              fontWeight: 400,
              lineHeight: 1.02,
              letterSpacing: "0.005em",
              color: t.ink,
              margin: 0,
            }}
          >
            {topic}
          </h1>

          {/* Instructor name — italic, drawn via clip-path inset reveal,
              with a gold underline that follows. */}
          <div style={{ marginTop: 44, position: "relative", width: "fit-content" }}>
            <div
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(22px, 2.4vw, 30px)",
                fontWeight: 400,
                letterSpacing: "0.04em",
                color: t.ink,
                clipPath: drawn ? "inset(0 0% 0 0%)" : "inset(0 50% 0 50%)",
                transition: reduce ? "none" : "clip-path 1.6s ease-out",
                willChange: reduce ? undefined : "clip-path",
              }}
            >
              {INSTRUCTOR_NAME}
            </div>
            {/* Gold underline beneath the italic name */}
            <div
              aria-hidden
              style={{
                marginTop: 8,
                height: 1,
                background: GOLD,
                width: underlined ? "100%" : 0,
                opacity: 0.85,
                transition: reduce ? "none" : "width 0.9s ease-out",
              }}
            />
          </div>

          {/* Bio one-liner */}
          <p
            style={{
              marginTop: 24,
              fontFamily: "var(--font-body)",
              fontStyle: "italic",
              fontSize: 17,
              lineHeight: 1.6,
              color: `${t.ink}cc`,
              maxWidth: 480,
            }}
          >
            {INSTRUCTOR_BIO_LINE}. Toshkentdagi PAYME, Uzum va boshqa
            mahsulotlarning interfeyslarini chizgan dizayner — bir kun
            ichida butun usulini ochib beradi.
          </p>

          {/* Date · venue micro line */}
          <div
            style={{
              marginTop: 56,
              display: "flex",
              alignItems: "center",
              gap: 14,
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.36em",
              color: t.ink,
            }}
          >
            <span>{t.date}</span>
            <span style={{ width: 24, height: 1, background: GOLD, opacity: 0.8 }} />
            <span>{t.venue.toUpperCase()}</span>
          </div>
        </div>
      </section>

      {/* ─── COUNTDOWN ───────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      <HairlineDivider ink={t.ink} />

      {/* ─── STORY — about the instructor (Uzbek copy) ──────────────── */}
      <Reveal reduce={reduce}>
        <StorySection
          t={t}
          eyebrow="murabbiy haqida"
          title="O'n besh yillik yo'l"
          body="Erkin aka 2010-yildan beri raqamli mahsulotlar dizaynida ishlaydi. Toshkentda boshlangan yo'l Berlin, Istanbul va Dubay studiyalaridan o'tdi. Uning qo'lidan o'tgan mahsulotlar orasida kunlik millionlab foydalanuvchini quvontiradigan to'lov ilovalari, e-tijorat platformalari va hukumat servislari bor. So'nggi besh yilda u boshqa dizaynerlarga o'rgatishga vaqt ajratmoqda — chunki ishonchi shu: yaxshi interfeys nafaqat texnik mahorat, balki ko'rib turgan har bir foydalanuvchiga hurmat. Ushbu masterklassda Erkin aka ana shu tajribani — gridi, ranglari, animatsiyasi, komponentlari bilan — bir kun ichida ochiq qilib beradi."
        />
      </Reveal>

      <HairlineDivider ink={t.ink} />

      {/* ─── CURRICULUM / SYLLABUS — UNIQUE SECTION ─────────────────── */}
      <Reveal reduce={reduce}>
        <section style={{ padding: "120px 32px", borderTop: `1px solid ${t.ink}22` }}>
          <div style={{ maxWidth: 920, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: GOLD,
                  marginBottom: 16,
                }}
              >
                DARS REJASI
              </div>
              <h2
                style={{
                  fontFamily: t.font,
                  fontStyle: "italic",
                  fontSize: "clamp(36px, 5vw, 56px)",
                  fontWeight: 400,
                  letterSpacing: "0.005em",
                  margin: 0,
                  color: t.ink,
                }}
              >
                To'rt modul · 7 soat
              </h2>
              <p
                style={{
                  marginTop: 16,
                  fontFamily: "var(--font-body)",
                  fontStyle: "italic",
                  fontSize: 16,
                  color: `${t.ink}99`,
                  maxWidth: 520,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                Bir kunlik dasturiy ko'rsatma — har bir modul mustaqil,
                lekin birgalikda butun bir ish jarayonini qamrab oladi.
              </p>
              <div
                style={{
                  width: 64,
                  height: 1,
                  background: GOLD,
                  margin: "24px auto 0",
                  opacity: 0.85,
                }}
              />
            </div>

            <div
              style={{
                borderBottom: `1px solid ${t.ink}22`,
              }}
            >
              {MODULES.map((m) => (
                <ModuleCard
                  key={m.no}
                  m={m}
                  ink={t.ink}
                  bg={t.bg}
                  font={t.font}
                  reduce={reduce}
                  onHover={onModuleHover}
                />
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      <HairlineDivider ink={t.ink} />

      {/* ─── INSTRUCTOR — UNIQUE SECTION ─────────────────────────────── */}
      <Reveal reduce={reduce}>
        <section
          style={{
            borderTop: `1px solid ${t.ink}22`,
            background: `${GOLD}08`,
          }}
        >
          <div
            className="murabbiy-instructor-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.1fr",
              alignItems: "stretch",
            }}
          >
            <div
              className="murabbiy-instructor-photo"
              style={{
                position: "relative",
                overflow: "hidden",
                background: t.ink,
                minHeight: 520,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url("${instructorPortraitSrc}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: "saturate(0.9) contrast(1.02)",
                }}
                aria-hidden
              />
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(180deg, ${t.ink}33, ${t.ink}77)`,
                  mixBlendMode: "multiply",
                }}
              />
              {/* Gold corner thread */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  top: 24,
                  left: 24,
                  width: 56,
                  height: 1,
                  background: GOLD,
                  opacity: 0.85,
                }}
              />
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  top: 24,
                  left: 24,
                  width: 1,
                  height: 56,
                  background: GOLD,
                  opacity: 0.85,
                }}
              />
            </div>

            <div
              className="murabbiy-instructor-text"
              style={{
                padding: "96px 64px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: GOLD,
                  marginBottom: 14,
                }}
              >
                MURABBIY HAQIDA
              </div>
              <h2
                style={{
                  fontFamily: t.font,
                  fontStyle: "italic",
                  fontSize: "clamp(40px, 5vw, 64px)",
                  fontWeight: 400,
                  lineHeight: 1.05,
                  margin: 0,
                  color: t.ink,
                }}
              >
                {INSTRUCTOR_NAME}
              </h2>
              <div
                style={{
                  marginTop: 12,
                  fontFamily: "var(--font-ui)",
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.28em",
                  color: `${t.ink}aa`,
                }}
              >
                SENIOR PRODUCT DESIGNER · 15+ YIL
              </div>

              <div style={{ width: 56, height: 1, background: GOLD, margin: "28px 0", opacity: 0.85 }} />

              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 17,
                  lineHeight: 1.7,
                  color: `${t.ink}d0`,
                  maxWidth: 560,
                  margin: 0,
                }}
              >
                Erkin aka faoliyatini 2010-yilda kichik veb-studiyada
                boshlagan. Bugungi kunda u Markaziy Osiyoda eng katta
                fintech va e-tijorat mahsulotlari ortidagi dizayn
                tizimlarini quradi. Mahorat tomonida eng ko'p
                qadrlaydigan narsasi — soddalik. "Foydalanuvchi
                aralashishni emas, oson o'tib ketishni xohlaydi" deydi.
              </p>

              <p
                style={{
                  marginTop: 18,
                  fontFamily: "var(--font-body)",
                  fontStyle: "italic",
                  fontSize: 15,
                  lineHeight: 1.65,
                  color: `${t.ink}aa`,
                  maxWidth: 560,
                  margin: "18px 0 0",
                }}
              >
                Berlinda Zalando, Toshkentda Uzum va Payme jamoalari
                bilan ishlagan; Behance va Dribbble portfoliosida 200+ ish.
              </p>

              {/* Past clients / studios */}
              <div
                style={{
                  marginTop: 40,
                  paddingTop: 24,
                  borderTop: `1px solid ${t.ink}22`,
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.4em",
                    color: `${t.ink}88`,
                    marginBottom: 16,
                  }}
                >
                  ISHLAGAN JAMOALARI
                </div>
                <div
                  className="murabbiy-clients"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 32,
                    alignItems: "center",
                  }}
                >
                  {CLIENTS.map((c, i) => (
                    <div
                      key={c}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 32,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: t.font,
                          fontSize: 18,
                          fontWeight: 500,
                          letterSpacing: "0.28em",
                          color: t.ink,
                        }}
                      >
                        {c}
                      </span>
                      {i < CLIENTS.length - 1 && (
                        <O.Diamond size={6} color={GOLD} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </Reveal>

      <HairlineDivider ink={t.ink} />

      {/* ─── SCHEDULE — single-day workshop ──────────────────────────── */}
      <Reveal reduce={reduce}>
        <ScheduleSection
          t={t}
          items={[
            ["10:00", "Eshiklar ochiladi · qahva va tanishuv"],
            ["10:30", "Modul 01 · UI fundamentals"],
            ["12:30", "Tushlik tanaffusi"],
            ["13:30", "Modul 02 · Component systems"],
            ["15:30", "Choy tanaffusi"],
            ["15:45", "Modul 03 · Animation language"],
            ["17:45", "Modul 04 · Q&A va portfolio review"],
            ["19:00", "Yakuniy networking · sertifikatlar"],
          ]}
        />
      </Reveal>

      <HairlineDivider ink={t.ink} />

      {/* ─── MAP ─────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine="IT Park Toshkent · Mustaqillik shoh ko'chasi 107A"
        />
      </Reveal>

      <HairlineDivider ink={t.ink} />

      {/* ─── RSVP — limited 25 seats ────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={setRsvpWithSound}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          slug={ctx.slug}
          label="Joyni band qiling — 25 ta o'rin"
        />
      </Reveal>

      <HairlineDivider ink={t.ink} />

      {/* ─── SHARE ───────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div
          onClick={(e) => {
            const target = e.target as HTMLElement | null;
            if (target && target.closest("button")) onShareTap();
          }}
        >
          <ShareSection t={t} />
        </div>
      </Reveal>

      <GuestFooter t={t} />
    </div>
  );
}
